import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { PAYMENT_METHODS } from "../../../utils/paymentMethods";
import { getSelectStyles } from "../../../utils/selectStyles";
import "../../accounts/create/CreateAccountPage.css";

export default function EditTransactionPage() {
  const { id } = useParams();

  const [accounts, setAccounts]           = useState([]);
  const [defaultAccount, setDefaultAccount] = useState(null);
  const [updated, setUpdated]             = useState(false);
  const [loading, setLoading]             = useState(true);
  const [submitting, setSubmitting]       = useState(false);
  const [error, setError]                 = useState(null);

  const [transaction, setTransaction] = useState({
    accountID: "", revenue: 0, profit: 0, notes: "",
    transactionDate: null, paymentMethod: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [txRes, accRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/transaction/${id}`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
        ]);
        const tx = { ...txRes.data, transactionDate: new Date(Number(txRes.data.transactionDate)).getTime() };
        setTransaction(tx);
        setAccounts(accRes.data);
        const match = accRes.data.find(a => a._id === tx.accountID);
        if (match) setDefaultAccount({ value: match._id, label: `${match.username}${match.email ? ` (${match.email})` : ''}` });
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load transaction");
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'transactionDate') {
      setTransaction(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setTransaction(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAccountSelect = (opt) => {
    if (!opt) return;
    setDefaultAccount({ value: opt.value, label: opt.label });
    setTransaction(prev => ({ ...prev, accountID: opt.value }));
  };

  const loadOptions = (inputValue, callback) => {
    const filtered = accounts.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const formatDateForInput = (unixMs) =>
    unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setUpdated(false);
    setSubmitting(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/transaction/${transaction._id}`,
        { ...transaction, revenue: Number(transaction.revenue), profit: Number(transaction.profit) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setUpdated(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update transaction");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Edit Transaction" />
      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div>
              <div className="form-panel-title">Edit Transaction</div>
              <div className="form-panel-subtitle">ID: {transaction._id}</div>
            </div>
          </div>

          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              <div className="form-section-label">Account & Date</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-fill" />Account</label>
                    <AsyncSelect
                      cacheOptions defaultOptions
                      loadOptions={loadOptions}
                      onChange={handleAccountSelect}
                      value={defaultAccount}
                      placeholder="Search account..."
                      isClearable
                      styles={getSelectStyles()}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-event" />Transaction Date</label>
                    <input className="form-input" type="date" name="transactionDate"
                      value={formatDateForInput(transaction.transactionDate)} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Financials</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-cash-stack" />Revenue</label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="revenue"
                        value={transaction.revenue} onChange={handleChange} step="0.01" min="0" required />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-graph-up-arrow" />Profit</label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="profit"
                        value={transaction.profit} onChange={handleChange} step="0.01" required />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Payment & Notes</div>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-credit-card" />Payment Method</label>
                    <select className="form-input" name="paymentMethod"
                      value={transaction.paymentMethod || ""} onChange={handleChange}>
                      <option value="">Select method…</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-journal-text" />Notes</label>
                    <textarea className="form-input" name="notes" value={transaction.notes || ''}
                      onChange={handleChange} rows={3} placeholder="Additional notes…"
                      style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {updated && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Transaction updated successfully!</div>}
              {error  && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button type="submit" className="btn-form-submit" disabled={submitting}>
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-check-lg'}`} />
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
